import React, { ReactNode } from 'react';
import classNames from 'classnames';
import Box from '../Box';
import IconArrowRight16 from '../icons/IconArrowRight16';
import Inline from '../Inline';
import Text, { ExtendableTextProps } from '../Text';

import styles from './imageTile.module.scss';

export type BackgroundImage = {
  /**
   * The src URL for the tile image.
   */
  imageUrl: string;

  /**
   * The alt text for the tile image.
   */
  altText: string;
};

interface CommonProps {
  /**
   * The main image for the tile.
   */
  backgroundImage: BackgroundImage | JSX.Element;

  /**
   * The URL to navigate to when the tile is clicked.
   */
  destinationUrl: string;

  /**
   * Whether or not the tile should have rounded corners (a non-zero border-radius).
   *
   * @default true
   */
  hasRoundedCorners?: boolean;

  /**
   * The headline of the image tile.
   */
  headline?: ReactNode;

  /**
   * Whether or not the tile should be full width within its container.
   *
   * @default false
   */
  isFullWidth?: boolean;

  /**
   * When `true` displays a loading state.
   */
  isLoading: boolean;

  /**
   * Callback to execute when a tile is clicked.
   * Helpful for app-specific analytics.
   */
  onClick?: () => void;

  /**
   * The size of the tile.
   *
   * - `large`: `343 x 428`, `100% x 428` full-width
   * - `small`: `237 x 346`, `100% x 346` full-width
   * - `inherited`: `100% x 100%` width and height limited by container defined styles
   */
  size: 'large' | 'small' | 'inherited';
}

interface CtaTextProps {
  /**
   * The call-to-action text for the lower banner.
   * This should not be passed when using the `labelText` prop.
   */
  ctaText?: string;

  labelText?: undefined;
}

interface LabelTextProps {
  ctaText?: undefined;

  /**
   * The label text under the headline.
   * This should not be passed when using the `ctaText` prop.
   */
  labelText?: string;
}

type TextProps = CtaTextProps | LabelTextProps;
export type ImageTileProps = CommonProps & TextProps;

/**
 * Image tiles send the user to a different page or experience. We use links inline with text and also as CTAs, meant to move the user along a flow.
 *
 * This prototype component was created by the LEO team.
 */
const ImageTile = ({
  backgroundImage,
  ctaText,
  destinationUrl,
  hasRoundedCorners = true,
  headline,
  isFullWidth = false,
  isLoading,
  labelText,
  onClick,
  size,
}: ImageTileProps) => {
  const className = classNames(styles.root, styles[size], {
    [styles['has-rounded-corners']]: hasRoundedCorners,
    [styles['is-full-width']]: isFullWidth,
    [styles['is-loading']]: isLoading,
  });

  let headlineSetting: ExtendableTextProps['setting'] = 'title-xsmall';

  if (size === 'small') {
    headlineSetting = 'title-xxsmall';
  } else if (isFullWidth) {
    headlineSetting = 'title-small';
  }

  let headlineText = headline && (
    <Box className={`${styles.headline} ${styles.custom}`}>{headline}</Box>
  );

  if (typeof headline === 'string') {
    headlineText = (
      <Text
        className={styles.headline}
        setting={headlineSetting}
        variant="knockout"
        as="p"
        spacingBottom={!labelText && !ctaText}
        data-testid="image-tile-headline"
      >
        {headline}
      </Text>
    );
  }

  if (isLoading)
    return <div data-testid="image-tile-loading" className={className} />;

  const image =
    'imageUrl' in backgroundImage ? (
      <img src={backgroundImage.imageUrl} alt={backgroundImage.altText} />
    ) : (
      backgroundImage
    );

  return (
    <a
      className={className}
      data-testid="image-tile"
      href={destinationUrl}
      onClick={onClick}
    >
      <div className={styles['background-hover-container']}>
        <div className={styles['gradient-overlay']} />
        <div className={styles.image}>{image}</div>
      </div>
      {headlineText}
      {ctaText && !labelText && (
        <Text
          className={styles['cta-container']}
          variant="knockout-inverse"
          setting="title-xxsmall"
        >
          <Box
            mb={1}
            as="span"
            style={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <Inline spacing={0.5} vAlign="center">
              {ctaText}
              <IconArrowRight16 color="inherit" purpose="decorative" />
            </Inline>
          </Box>
        </Text>
      )}
      {labelText && !ctaText && (
        <Box className={styles['eyebrow-small']} mt={0.25} bgColor="gray-16">
          <Text color="white" setting="eyebrow-small">
            {labelText}
          </Text>
        </Box>
      )}
    </a>
  );
};

export default ImageTile;
